import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import { withLoading } from '@ps/core-loading';
import type {
  FlexibilityPrices,
  SupplierAllocation,
  SupplierAllocationsParams,
  SupplierAllocationsUpdate,
  SupplierBlend,
} from '@ps/survey-utils';

import {
  SupplierAllocationDto,
  SupplierFlexibilityPricesDto,
} from './dtos/supplier.dto';
import { SupplierBlendDto } from './dtos/supplier-blend.dto';
import { SupplierAllocationMapper } from './mappers/supplier.mapper';
import { SupplierBlendMapper } from './mappers/supplier-blend.mapper';
import { FlexibilityPricesMapper } from './mappers/flexibility-prices.mapper';
import { SupplierAllocationsParamsPayloadMapper } from './mappers/supplier-allocations-params.payload.mapper';
import { SupplierAllocationsBodyPayloadMapper } from './mappers/supplier-allocations-body.payload.mapper';

@Injectable({ providedIn: 'root' })
export class SupplierApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = `${this._env.pureSpectrum.url}`;
  private readonly _mapper = new SupplierAllocationMapper();
  private readonly _blendMapper = new SupplierBlendMapper();
  private readonly _flexibilityPricesMapper = new FlexibilityPricesMapper();
  private readonly _paramsMapper = new SupplierAllocationsParamsPayloadMapper();
  private readonly _bodyMapper = new SupplierAllocationsBodyPayloadMapper();

  public flexibilityPriceParams(
    surveyId: number,
  ): Observable<FlexibilityPrices> {
    return this._http
      .get<SupplierFlexibilityPricesDto>(
        `${this._baseUrl}/supplier/getFlexibilityPricesParams/${surveyId}`,
        { context: withLoading('drawer') },
      )
      .pipe(map((dto) => this._flexibilityPricesMapper.fromDto(dto)));
  }

  public blends(locale: string): Observable<SupplierBlend[]> {
    return this._http
      .get<
        SupplierBlendDto[]
      >(`${this._baseUrl}/buyers/v2/supplier-blends`, { params: new HttpParams().set('locale', locale), context: withLoading('drawer') })
      .pipe(map((dtos) => this._blendMapper.fromDtoList(dtos)));
  }

  public allocations(
    surveyId: number,
    params: SupplierAllocationsParams,
  ): Observable<SupplierAllocation[]> {
    const httpParams = new HttpParams({
      fromObject: { ...this._paramsMapper.toPayload(params) },
    });

    return this._http
      .get<
        SupplierAllocationDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/${surveyId}/suppliers`, { params: httpParams })
      .pipe(map((dtos) => this._mapper.fromDtoList(dtos)));
  }

  /**
   * Both the query params and the body come off the one `update` command, so a
   * caller cannot hand this a scope that disagrees with the rows being sent.
   *
   * The response is the same shape as `allocations()`'s GET — the backend
   * returns the survey's updated supplier list, not just an ack.
   */
  public updateAllocations(
    surveyId: number,
    update: SupplierAllocationsUpdate,
  ): Observable<SupplierAllocation[]> {
    const httpParams = new HttpParams({
      fromObject: {
        ...this._paramsMapper.toPayload({
          scope: 'update',
          context: update.context,
        }),
      },
    });

    return this._http
      .put<
        SupplierAllocationDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/${surveyId}/suppliers`, this._bodyMapper.toPayload(update), { params: httpParams, context: withLoading('drawer') })
      .pipe(map((dtos) => this._mapper.fromDtoList(dtos)));
  }
}
